import { invariant } from './invariant';

export async function readWholeStream(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader = stream.getReader();
  const chunks: Uint8Array[] = [];
  let totalSize = 0;
  while (true) {
    const readRes = await reader.read();
    if (readRes.done) {
      break;
    }
    chunks.push(readRes.value);
    totalSize += readRes.value.length;
  }
  return concatChunks(chunks, totalSize);
}

export async function readableStreamToArray<T>(stream: ReadableStream<T>): Promise<T[]> {
  const reader = stream.getReader();
  const chunks: T[] = [];
  while (true) {
    const readRes = await reader.read();
    if (readRes.done) {
      break;
    }
    chunks.push(readRes.value);
  }
  return chunks;
}

export function concatChunks(chunks: Uint8Array[], totalSize?: number): Uint8Array {
  if (totalSize === undefined) {
    totalSize = chunks.reduce((total, chunk) => total + chunk.length, 0);
  }
  const res = new Uint8Array(totalSize);
  let offset = 0;
  for (const chunk of chunks) {
    res.set(chunk, offset);
    offset += chunk.length;
  }
  return res;
}


export function sliceChunkArray(chunks: Uint8Array[], inclusiveStart: number, exclusiveEnd: number): Uint8Array[] {
  const output: Uint8Array[] = [];
  let totalSize = 0;
  chunks.forEach(c => totalSize += c.byteLength);

  const expectedSize = exclusiveEnd - inclusiveStart;

  invariant(inclusiveStart <= totalSize, () => `sliceChunkArray cannot get slice [${inclusiveStart};${exclusiveEnd}[ from ${totalSize} input bytes`);

  // First, let's truncate at the start of the data
  let bytesToRemoveFromTheStart = inclusiveStart;

  for (let i = 0; i < chunks.length; i++) {
    const chunk = chunks[i];
    if (!chunk) {
      continue;
    }
    if (bytesToRemoveFromTheStart === 0) {
      // We have already removed all the bytes we needed to remove from the beginning,
      // let's copy the remaining chunks as is
      output.push(chunk);
    } else if (chunk.byteLength <= bytesToRemoveFromTheStart) {
      // This chunk is smaller than what we need to remove, so let's
      // remove it entirely by not adding it to the output array
      bytesToRemoveFromTheStart -= chunk.byteLength;
    } else {
      // The chunk is larger than what we need to remove, so let's slice it
      output.push(chunk.slice(bytesToRemoveFromTheStart));
      bytesToRemoveFromTheStart = 0;
    }
  }


  totalSize -= inclusiveStart;
  let bytesToRemove = totalSize - expectedSize;
  invariant(bytesToRemove >= 0, () => `sliceChunkArray cannot get slice [${inclusiveStart};${exclusiveEnd}[ from ${totalSize} input bytes`);

  if (bytesToRemove === 0) {
    return output;
  }

  let exclusiveEndOfOutput = output.length;

  // Then, let's truncate at the end of the data
  for (let j = output.length - 1; bytesToRemove > 0 && j >= 0; j--) {
    const chunk = output[j];
    if (!chunk) {
      continue;
    }
    if (chunk.length <= bytesToRemove) {
      // We need to remove this whole chunk
      exclusiveEndOfOutput--;
      bytesToRemove -= chunk.byteLength;
    } else if (chunk.byteLength > bytesToRemove) {
      // We need to remove part of this chunk;
      output[j] = chunk.slice(0, chunk.byteLength - bytesToRemove);
      bytesToRemove = 0;
    }
  }

  return output.slice(0, exclusiveEndOfOutput);
}

